import React, { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { ErrorMessage, HelperMessage } from '@atlaskit/form';
import { fg } from '@atlaskit/platform-feature-flags';
import SmartUserPicker, {
	type OnChange,
	type Value as PickerUserValue,
	type User as UserPickerUser,
	UserType,
} from '@atlaskit/smart-user-picker';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { token } from '@atlaskit/tokens';

import { type InvitedUser } from '../../types/user';
import { DEBOUNCE_MS, MAX_MEMBERS } from '../../utils/constants';
import { getUserDomain } from '../../utils/get-user-domain';
import { messages } from '../i18n';
import { type MemberPickerProps } from '../MemberPicker/types';
import { CPUS_SEARCH_QUERY_FILTER } from '../MemberPicker/utils';

const SMART_USER_PICKER_ID = 'AddMemberToTeam';
const LOGGED_IN_USER = 'Context';

export function MemberPicker(props: MemberPickerProps): React.JSX.Element {
	const {
		onChange,
		maxSelectedMembers = MAX_MEMBERS,
		cloudId,
		initialValues,
		value,
		teamId,
		filterUsers,
		extraAnalyticsAttrs,
		showHelperMessages = true,
		isInvalid,
		ariaLabelledBy,
		ariaDescribedBy,
		inputId,
		currentUserEmail,
		allowEmailInvites,
		bootstrapOptions,
	} = props;

	const [hasError, setError] = useState(false);
	const [isMaximum, setIsMaximum] = useState(false);
	const [isDisabled, setDisabled] = useState(false);
	const [isOpen, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const { formatMessage } = useIntl();
	const [removedInitialValues, setRemovedInitialValues] = useState<Set<string>>(new Set());
	const { fireEvent } = useAnalyticsEvents();

	const handleOnChange: OnChange = useCallback(
		(value: PickerUserValue) => {
			const selectedUsers = (value || []) as UserPickerUser[];
			if (initialValues?.length) {
				const currentIds = new Set(selectedUsers.map((user) => user.id));
				initialValues.forEach((initUser) => {
					if (!currentIds.has(initUser.id)) {
						setRemovedInitialValues((prev) => new Set([...prev, initUser.id]));
					}
				});
			}

			setDisabled(selectedUsers.length >= maxSelectedMembers);

			setIsMaximum(selectedUsers.length === maxSelectedMembers);

			const isError = selectedUsers.length > maxSelectedMembers;
			setError(isError);
			fireEvent('track.memberPicker.error', extraAnalyticsAttrs || {});

			if (onChange) {
				onChange(selectedUsers, { hasError: isError, isDisabled });
			}
		},
		[isDisabled, maxSelectedMembers, onChange, extraAnalyticsAttrs, initialValues, fireEvent],
	);

	const handleOnInputChange = useCallback(
		(query?: string) => {
			setQuery(query || '');
		},
		[setQuery],
	);

	const handleOnBlur = useCallback(() => {
		setOpen(false);
	}, [setOpen]);

	const handleOnFocus = useCallback(() => {
		setOpen(true);
	}, [setOpen]);

	// convert InvitedUser type to (OptionalData | User) type of @atlaskit/user-picker
	const memoizedValue = useMemo(() => {
		const valueUsers = value || [];

		if (initialValues?.length) {
			const uniqueInitialUsers = initialValues.filter(
				(initUser) =>
					!valueUsers.some((valUser) => valUser.id === initUser.id) &&
					!removedInitialValues.has(initUser.id),
			);
			const allUsers = [...valueUsers, ...uniqueInitialUsers];

			if (!allUsers.length) {
				return undefined;
			}

			return allUsers.map((user: InvitedUser) => ({
				name: user.fullName,
				publicName: user.nickname,
				type: UserType,
				...user,
			})) as UserPickerUser[];
		}

		// If no initial values, just use current values
		if (valueUsers.length) {
			return valueUsers.map((user: InvitedUser) => ({
				name: user.fullName,
				publicName: user.nickname,
				type: UserType,
				...user,
			})) as UserPickerUser[];
		}
	}, [value, initialValues, removedInitialValues]);

	const propsWhenHasError = hasError ? { open: false, addMoreMessage: '' } : {};

	return (
		<div>
			<SmartUserPicker
				ariaLabelledBy={ariaLabelledBy}
				aria-describedby={ariaDescribedBy}
				disableInput={isDisabled}
				onChange={handleOnChange}
				onInputChange={handleOnInputChange}
				onBlur={handleOnBlur}
				onFocus={handleOnFocus}
				open={allowEmailInvites ? undefined : isOpen && query.length > 0}
				includeTeams={false}
				inputId={inputId}
				fieldId={SMART_USER_PICKER_ID}
				principalId={LOGGED_IN_USER}
				isMulti
				placeholder={formatMessage(messages.userPickerPlaceholderNext)}
				addMoreMessage={allowEmailInvites ? formatMessage(messages.enterMore) : ''}
				noOptionsMessage={() =>
					formatMessage(
						allowEmailInvites
							? messages.userPickerNoOptionWithEmailSuggestion
							: messages.userPickerNoOption,
					)
				}
				allowEmail={allowEmailInvites ? true : undefined}
				suggestEmailsForDomain={allowEmailInvites ? getUserDomain(currentUserEmail) : undefined}
				productKey="people"
				siteId={cloudId}
				value={memoizedValue}
				debounceTime={DEBOUNCE_MS}
				width="100%"
				containerId={teamId || ''}
				searchQueryFilter={CPUS_SEARCH_QUERY_FILTER}
				filterOptions={filterUsers}
				maxOptions={MAX_MEMBERS}
				bootstrapOptions={
					fg('jira_team_experience_improvements_feature_gate') ? bootstrapOptions : []
				}
				showClearIndicator={true}
				//@eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
				styles={{
					multiValue: (style) => ({
						...style,
						borderRadius: token('radius.small'),
						backgroundColor: token('color.background.neutral.subtle'),
						border: `${token('border.width')} solid ${token('color.border.input')}`,
					}),
					control: (style, state) => ({
						...style,
						backgroundColor: token('color.background.neutral.subtle'),
						borderColor:
							isInvalid && !state.isFocused ? token('color.border.danger') : style.borderColor,
						':hover': {
							...style[':hover'],
							borderColor:
								isInvalid && !state.isFocused ? token('color.border.danger') : style.borderColor,
						},
						borderWidth: token('border.width', isInvalid ? '2px' : '1px'),
						minHeight: '40px',
						'.fabric-user-picker__clear-indicator': { opacity: 1 },
					}),
				}}
				{...propsWhenHasError}
			/>

			{showHelperMessages &&
				(hasError ? (
					<ErrorMessage>
						{formatMessage(messages.limitedUsersInvitationMessageError, {
							maxNumber: maxSelectedMembers,
						})}
					</ErrorMessage>
				) : isMaximum ? (
					<ErrorMessage>
						{formatMessage(messages.limitedUsersInvitationMessageMaximumError, {
							maxNumber: maxSelectedMembers,
						})}
					</ErrorMessage>
				) : (
					<HelperMessage>
						{formatMessage(messages.limitedUsersInvitationMessageInfo, {
							maxNumber: maxSelectedMembers,
						})}
					</HelperMessage>
				))}
		</div>
	);
}

export default MemberPicker;
